import { admininsterInvoice, downloadInvoicePdf, getInvoices } from 'APIs/invoices/invoices';
import { Invoice, InvoicesQueryParams } from 'APIs/invoices/invoices.types';
import { QueryParams } from 'APIs/types';
import FilterComponent from 'Components/Common/Filters/FilterComponent';
import { FilterOption } from 'Components/Common/Filters/types';
import { FunctionComponent, ReactElement, useCallback, useEffect, useState } from 'react';
import { MdFilterList, MdRefresh } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getErrorMessage from 'utils/getErrorMessage';

import InvoiceDataTable from './InvoiceDataTable';
import { filterQueryParams, initFilterOptions } from './InvoiceFilter';
import { FilterContainer } from './styles';

type Props = {
  merchantId?: string;
};

const InvoiceList = ({ merchantId }: Props): ReactElement => {
  const RefreshIcon = MdRefresh as FunctionComponent;
  const FilterListIcon = MdFilterList as FunctionComponent;

  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [actionInvoices, setActionInvoices] = useState<{ [key: string]: boolean }>({});
  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<Array<Invoice>>([]);
  const [filterOptionValues, setFilterOptionValues] = useState<FilterOption[]>(initFilterOptions);
  const [activePage, setActivePage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filterCount, setFilterCount] = useState<number>(initFilterOptions.filter((option) => option.checked).length);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchingError, setFetchingError] = useState<string>('');

  const getFilterQueryParams = useCallback(filterQueryParams, []);

  const fetchInvoices = useCallback(async () => {
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      setIsLoading(true);

      const qParams: QueryParams = getFilterQueryParams(filterOptionValues);

      const resp = await getInvoices(apiAprilBaseUri, {
        ...(qParams as InvoicesQueryParams),
        merchantId: merchantId,
        page: activePage,
        limit: perPage,
        sort: '-createdAt',
      });
      setInvoices(resp.items);
      setTotalCount(resp.totalRecords);
    } catch (e) {
      setInvoices([]);
      setTotalCount(0);
      setFetchingError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiAprilBaseUri, filterOptionValues, getFilterQueryParams, merchantId, perPage]);

  const handlePerPageChange = useCallback((newPerPage: number, page: number) => {
    setPerPage(newPerPage);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleAdministerInvoice = useCallback(
    async (invoiceId: string, action: 'download' | 'resend_email' | 'mark_as_manually_paid') => {
      try {
        if (!apiAprilBaseUri) return;

        const confirmMessages = {
          resend_email: `Are you sure you want to resend the email for the invoice ${invoiceId}?`,
          mark_as_manually_paid: `Are you sure you want to mark this invoice (#${invoiceId}) as manually paid?`,
        };
        const confimed = action !== 'download' ? window.confirm(confirmMessages[action]) : true;

        if (!confimed) return;

        setActionInvoices((prev) => ({ ...prev, [invoiceId]: true }));

        if (action === 'download') {
          const url = await downloadInvoicePdf(apiAprilBaseUri, invoiceId);

          if (url) {
            const a = document.createElement('a');
            a.href = url;
            a.download = `${invoiceId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } else {
            toast.error('Error: Unable to downlaod the file');
          }
        } else if (action === 'resend_email') {
          await admininsterInvoice(apiAprilBaseUri, invoiceId, {
            SendLatestMerchantInvoiceEmailRequest: {},
          });
          toast.success('Invoice email resent successfully');
        } else if (action === 'mark_as_manually_paid') {
          await admininsterInvoice(apiAprilBaseUri, invoiceId, {
            MarkMerchantInvoiceAsPaidRequest: {},
          });
          fetchInvoices();
          toast.success('Invoice marked as manually paid successfully');
        }
      } catch (e) {
        toast.error(getErrorMessage(e));
      } finally {
        setActionInvoices((prev) => {
          const { [invoiceId]: _, ...newData } = prev;
          return newData;
        });
      }
    },
    [apiAprilBaseUri, fetchInvoices],
  );

  useEffect(() => {
    fetchInvoices();
  }, [activePage, perPage, fetchInvoices]);

  return (
    <div>
      <h3 className="mb-0">Invoices</h3>
      <FilterContainer>
        <button className="lp-w-full" onClick={() => fetchInvoices()}>
          Refresh <RefreshIcon />
        </button>
        <button className="lp-w-full" onClick={() => setFilterPopupOpen((prev) => !prev)}>
          Filter {filterCount > 0 && `(${filterCount})`} <FilterListIcon />
        </button>
        {filterPopupOpen && (
          <FilterComponent
            filterOptionValues={filterOptionValues}
            initFilterOptionValues={initFilterOptions}
            setFilterOptionValues={setFilterOptionValues}
            handlePageChange={setActivePage}
            handleFilterCount={setFilterCount}
            toggleFilter={() => setFilterPopupOpen(false)}
          />
        )}
      </FilterContainer>
      <InvoiceDataTable
        invoiceList={invoices}
        totalRows={totalCount}
        handlePageChange={handlePageChange}
        handlePerRowsChange={handlePerPageChange}
        fetchListLoader={isLoading}
        limit={perPage}
        activePage={activePage}
        isErrorRequest={!!fetchingError}
        hideMerchantColumn={!!merchantId}
        handleAdministerInvoice={handleAdministerInvoice}
        actionInvoices={actionInvoices}
        actionStates={['outstanding', 'overdue', 'near_due', 'due']}
      />
    </div>
  );
};

export default InvoiceList;
